import { ChevronRight, Clock3, PackageOpen } from 'lucide-react';

import FulfillmentGroupBadge from './FulfillmentGroupBadge';
import {
  formatOrderItems,
  formatOrderMoney,
  formatWaitingAge,
  orderPresentation,
} from '../../utils/ordersOperationsPresentation';

function Payment({ payment, total }) {
  return (
    <div>
      <p className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">{formatOrderMoney(total)}</p>
      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
        {payment?.method || 'Payment unavailable'} · {payment?.status || 'Status unavailable'}
      </p>
    </div>
  );
}

export default function OrderOperationsTable({
  items = [],
  onOpen,
  emptyTitle = 'No orders in this queue',
  emptyDescription = 'Orders appear here when they reach this operational stage.',
}) {
  if (items.length === 0) {
    return (
      <div className="flex min-h-56 flex-col items-center justify-center px-6 py-10 text-center">
        <PackageOpen className="h-8 w-8 text-neutral-400" aria-hidden="true" />
        <h2 className="mt-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">{emptyTitle}</h2>
        <p className="mt-1 max-w-md text-sm leading-6 text-neutral-500 dark:text-neutral-400">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[720px] border-collapse bg-surface text-left">
          <thead className="border-y border-subtle bg-surface-sunken text-[11px] font-semibold uppercase tracking-[0.07em] text-secondary">
            <tr>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Customer & items</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Next action</th>
              <th className="px-4 py-3">Waiting</th>
              <th className="w-16 px-4 py-3"><span className="sr-only">Open</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-subtle">
            {items.map(item => {
              const presentation = orderPresentation(item);
              return (
                <tr key={item.orderId} className="bg-surface hover:bg-hover">
                  <td className="px-4 py-3.5 align-top">
                    <p className="whitespace-nowrap font-semibold text-neutral-950 dark:text-white">{item.orderReference}</p>
                    <p className="mt-1 text-xs text-neutral-500">{item.checklist?.packedQuantity || 0}/{item.checklist?.requiredQuantity || 0} packed</p>
                  </td>
                  <td className="max-w-[22rem] px-4 py-3.5 align-top">
                    <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">{item.customer || 'Customer unavailable'}</p>
                    <p className="mt-1 truncate text-xs text-neutral-500 dark:text-neutral-400">{formatOrderItems(item.items)}</p>
                  </td>
                  <td className="px-4 py-3.5 align-top"><Payment payment={item.payment} total={item.total} /></td>
                  <td className="max-w-[18rem] px-4 py-3.5 align-top">
                    <FulfillmentGroupBadge group={presentation.group} label={presentation.label} />
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm tabular-nums text-neutral-600 dark:text-neutral-300">
                      <Clock3 className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                      {formatWaitingAge(item.ageSeconds)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    <button type="button" onClick={() => onOpen(item)} aria-label={`Open ${item.orderReference}`} className="grid h-10 w-10 place-items-center rounded-lg border border-default text-secondary hover:bg-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="divide-y divide-neutral-200 lg:hidden dark:divide-neutral-800">
        {items.map(item => {
          const presentation = orderPresentation(item);
          return (
            <button key={item.orderId} type="button" onClick={() => onOpen(item)} className="block w-full bg-surface p-4 text-left hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus" aria-label={`Open ${item.orderReference}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-neutral-950 dark:text-white">{item.orderReference}</p>
                  <p className="mt-1 truncate text-sm text-neutral-600 dark:text-neutral-300">{item.customer || 'Customer unavailable'}</p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 text-xs tabular-nums text-neutral-500">
                  <Clock3 className="h-3.5 w-3.5" aria-hidden="true" /> {formatWaitingAge(item.ageSeconds)}
                </span>
              </div>
              <p className="mt-2 line-clamp-2 text-xs leading-5 text-neutral-500 dark:text-neutral-400">{formatOrderItems(item.items)}</p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <FulfillmentGroupBadge group={presentation.group} label={presentation.label} />
                <div className="text-right"><Payment payment={item.payment} total={item.total} /></div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}
